import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { POOL_STATE_LABELS, dt } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Boxes, AlertTriangle, Plus, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const STATES = ["CLEAN_AVAILABLE", "AT_CUSTOMER", "DIRTY", "IN_PROCESS", "DAMAGED", "DISCARDED"];

export default function Pool() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pool");
  const [pool, setPool] = useState(null);
  const [movements, setMovements] = useState(null);
  const [articles, setArticles] = useState([]);
  const [proc, setProc] = useState({ article_id: "", qty: "" });
  const [procOpen, setProcOpen] = useState(false);
  const [mv, setMv] = useState({ article_id: "", from_state: "DIRTY", to_state: "IN_PROCESS", qty: "" });

  const load = useCallback(() => {
    api.get("/inventory/pool").then((r) => setPool(r.data));
    api.get("/inventory/movements").then((r) => setMovements(r.data));
    api.get("/catalog/articles").then((r) => setArticles(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const canManage = user.role !== "operator";

  const procure = async () => {
    try { await api.post("/inventory/procure", { article_id: proc.article_id, qty: +proc.qty }); toast.success("Stock immesso"); setProcOpen(false); setProc({ article_id: "", qty: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const move = async () => {
    try { await api.post("/inventory/move", { article_id: mv.article_id, from_state: mv.from_state, to_state: mv.to_state, qty: +mv.qty }); toast.success("Movimento registrato"); setMv({ ...mv, qty: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Biancheria" subtitle="Pool, lavorazione e movimenti di magazzino">
        {canManage && tab === "pool" && (
          <Dialog open={procOpen} onOpenChange={setProcOpen}>
            <DialogTrigger asChild><Button className="bg-[#0B2447]" data-testid="procure-btn"><Plus size={16} className="mr-1" />Immetti stock</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Immissione stock (nuovo pulito)</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Articolo</Label>
                  <Select value={proc.article_id} onValueChange={(v) => setProc({ ...proc, article_id: v })}>
                    <SelectTrigger className="mt-1" data-testid="procure-article"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                    <SelectContent>{articles.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Quantità</Label><Input type="number" data-testid="procure-qty" className="mt-1" value={proc.qty} onChange={(e) => setProc({ ...proc, qty: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={procure} className="bg-[#0B2447]">Immetti</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="pool" data-testid="tab-pool">Pool</TabsTrigger>
          <TabsTrigger value="lavorazione" data-testid="tab-lavorazione">Lavorazione</TabsTrigger>
          <TabsTrigger value="movimenti" data-testid="tab-movimenti">Movimenti</TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "pool" && (!pool ? <Loading /> : (
        <div className="space-y-3">
          {pool.map((p) => (
            <Card key={p.article_id} className={`p-4 ${p.alert ? "border-orange-300 bg-orange-50/40" : ""}`} data-testid={`pool-${p.article_id}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="font-display font-bold text-[#0B2447] flex items-center gap-2">{p.article_name}{p.alert && <AlertTriangle size={16} className="text-orange-500" />}</p>
                <p className="text-sm text-slate-400">Totale pool: <span className="font-bold text-[#0B2447]">{p.total}</span></p>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {STATES.map((s) => (
                  <div key={s} className={`p-2 rounded-lg text-center ${s === "CLEAN_AVAILABLE" && p.alert ? "bg-orange-100" : "bg-slate-50"}`}>
                    <p className="stat-num text-lg text-[#0B2447]">{p.states[s]}</p>
                    <p className="text-[10px] text-slate-400 leading-tight">{POOL_STATE_LABELS[s]}</p>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      ))}

      {tab === "lavorazione" && (
        <Card className="p-6 max-w-2xl">
          <p className="font-display font-bold text-[#0B2447] mb-4">Sposta biancheria tra stati</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Articolo</Label>
              <Select value={mv.article_id} onValueChange={(v) => setMv({ ...mv, article_id: v })}>
                <SelectTrigger className="mt-1" data-testid="move-article"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{articles.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quantità</Label><Input type="number" data-testid="move-qty" className="mt-1" value={mv.qty} onChange={(e) => setMv({ ...mv, qty: e.target.value })} /></div>
            <div><Label>Da</Label>
              <Select value={mv.from_state} onValueChange={(v) => setMv({ ...mv, from_state: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{POOL_STATE_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>A</Label>
              <Select value={mv.to_state} onValueChange={(v) => setMv({ ...mv, to_state: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{STATES.map((s) => <SelectItem key={s} value={s}>{POOL_STATE_LABELS[s]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 mt-4 flex-wrap">
            <Button variant="outline" size="sm" onClick={() => setMv({ ...mv, from_state: "DIRTY", to_state: "IN_PROCESS" })}>Sporco <ArrowRight size={12} className="mx-1" /> In lavorazione</Button>
            <Button variant="outline" size="sm" onClick={() => setMv({ ...mv, from_state: "IN_PROCESS", to_state: "CLEAN_AVAILABLE" })}>In lavorazione <ArrowRight size={12} className="mx-1" /> Pulito</Button>
          </div>
          <Button onClick={move} data-testid="move-submit" className="mt-4 bg-[#0EA5C4] hover:bg-[#0c8ba7]">Registra movimento</Button>
        </Card>
      )}

      {tab === "movimenti" && (!movements ? <Loading /> : movements.length === 0 ? <EmptyState icon={Boxes} title="Nessun movimento" /> : (
        <Card className="divide-y">
          {movements.map((m) => (
            <div key={m.id} className="flex items-center justify-between px-4 py-3 text-sm">
              <div><p className="font-semibold text-[#0B2447]">{m.article_name}</p><p className="text-xs text-slate-400">{m.reason} · {dt(m.created_at)}</p></div>
              <div className="flex items-center gap-2 text-slate-500">
                <span className="text-xs">{m.from_state ? POOL_STATE_LABELS[m.from_state] : "Immesso"}</span>
                <ArrowRight size={12} /><span className="text-xs">{POOL_STATE_LABELS[m.to_state]}</span>
                <span className="font-bold text-[#0B2447] ml-2">{m.qty}</span>
              </div>
            </div>
          ))}
        </Card>
      ))}
    </div>
  );
}
